import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { MINDSET_SIDEBAR_ID, MINDSET_FLOATING_ID, addMindsetDockAgentIfRequired, useCheckIfWeShowAgentInPage, useMindsetSituationalAwareness, useMindsetPageTools, isMindsetAgentDockHidden, MINDSET_HIDE_AGENT_DOCK_EVENT, MINDSET_SHOW_AGENT_DOCK_EVENT } from 'utils/mindset';
import { getCoachAiName } from 'utils/i18n';
import useBreakpoints from 'hooks/useBreakpoints';
import { useOpenMindsetAgent } from 'utils/mindset/useOpenMindsetAgent';
import { makeStartDrag } from 'components/MindsetAgentDock/startDrag';
import { useOpenMindsetWithMessageEvent } from 'utils/mindset/useOpenMindsetWithMessageEvent';
import { FloatingDock } from './FloatingDock';
import { SidebarDock } from './SidebarDock';
import { useFloatingGeometry } from './useFloatingGeometry';
import { useSidebarGeometry } from './useSidebarGeometry';

// 'floating' has no current caller - it's scaffolded ahead of a planned future
// consumer, not dead code.
import { jsx as _jsx } from "react/jsx-runtime";
export function MindsetAgentDock(_ref) {
  var _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? 'sidebar' : _ref$variant;
  var isFloating = variant === 'floating';
  var containerId = isFloating ? MINDSET_FLOATING_ID : MINDSET_SIDEBAR_ID;
  var startDrag = makeStartDrag(containerId);
  var _useCheckIfWeShowAgen = useCheckIfWeShowAgentInPage(),
    hasAgentOnPage = _useCheckIfWeShowAgen.hasAgentOnPage;
  var _useState = useState(function () {
      return isMindsetAgentDockHidden();
    }),
    _useState2 = _slicedToArray(_useState, 2),
    isHiddenByPage = _useState2[0],
    setIsHiddenByPage = _useState2[1];
  var _useBreakpoints = useBreakpoints('m'),
    isDesktop = _useBreakpoints.m;
  useLayoutEffect(function () {
    setIsHiddenByPage(isMindsetAgentDockHidden());
    function onHide() {
      setIsHiddenByPage(true);
    }
    function onShow() {
      setIsHiddenByPage(false);
    }
    window.addEventListener(MINDSET_HIDE_AGENT_DOCK_EVENT, onHide);
    window.addEventListener(MINDSET_SHOW_AGENT_DOCK_EVENT, onShow);
    return function () {
      window.removeEventListener(MINDSET_HIDE_AGENT_DOCK_EVENT, onHide);
      window.removeEventListener(MINDSET_SHOW_AGENT_DOCK_EVENT, onShow);
    };
  }, []);
  var _useOpenMindsetAgent = useOpenMindsetAgent(containerId, function () {
      return addMindsetDockAgentIfRequired(containerId);
    }),
    loadingStatus = _useOpenMindsetAgent.loadingStatus,
    setLoadingStatus = _useOpenMindsetAgent.setLoadingStatus,
    isOpen = _useOpenMindsetAgent.isOpen,
    setIsOpen = _useOpenMindsetAgent.setIsOpen,
    hasInitialized = _useOpenMindsetAgent.hasInitialized,
    abortedInitCount = _useOpenMindsetAgent.abortedInitCount,
    ensureInitialized = _useOpenMindsetAgent.ensureInitialized,
    handleOpen = _useOpenMindsetAgent.handleOpen;
  useMindsetSituationalAwareness(hasInitialized, containerId);
  var _useMindsetPageTools = useMindsetPageTools(hasInitialized, containerId),
    modals = _useMindsetPageTools.modals;

  // Skips events that would initialize this dock's container while hidden -
  // display:none breaks the embedded agent's rendering.
  // Covers a closed mobile panel (silent priming) and a page-embedded card owning the event.
  var handleOpenWithMessage = useCallback(/*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(message, silent, threadTitle, coachingContext) {
      return _regeneratorRuntime.wrap(function (_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            if (!isHiddenByPage) {
              _context.next = 1;
              break;
            }
            return _context.abrupt("return", false);
          case 1:
            if (!(silent && !isDesktop)) {
              _context.next = 2;
              break;
            }
            return _context.abrupt("return", false);
          case 2:
            _context.next = 3;
            return handleOpen(message, silent, threadTitle, coachingContext);
          case 3:
            return _context.abrupt("return", true);
          case 4:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function (_x, _x2, _x3, _x4) {
      return _ref2.apply(this, arguments);
    };
  }(), [handleOpen, isDesktop, isHiddenByPage]);
  useOpenMindsetWithMessageEvent(handleOpenWithMessage);
  var sidebarGeometry = useSidebarGeometry({
    startDrag: startDrag,
    isActive: !isFloating
  });
  var floatingGeometry = useFloatingGeometry({
    startDrag: startDrag,
    isActive: isFloating
  });
  var canAutoInit = isDesktop || isOpen;

  // abortedInitCount re-runs this once an init has given up on a container
  // that was replaced mid-flight, so a panel that came back - reopened, or
  // resized to desktop - gets a fresh attempt instead of the aborted one's
  // spinner.
  useEffect(function () {
    if (isFloating || !hasAgentOnPage || isHiddenByPage || hasInitialized) return;
    if (!canAutoInit) return;
    ensureInitialized();
  }, [isFloating, hasAgentOnPage, isHiddenByPage, hasInitialized, canAutoInit, abortedInitCount, ensureInitialized]);
  if (!hasAgentOnPage) {
    return null;
  }
  var coachAiName = getCoachAiName();
  if (isFloating) {
    return /*#__PURE__*/_jsx(FloatingDock, {
      containerId: containerId,
      isOpen: isOpen,
      setIsOpen: setIsOpen,
      isHiddenByPage: isHiddenByPage,
      hasInitialized: hasInitialized,
      loadingStatus: loadingStatus,
      setLoadingStatus: setLoadingStatus,
      handleOpen: handleOpen,
      coachAiName: coachAiName,
      modals: modals,
      geometry: floatingGeometry
    });
  }
  return /*#__PURE__*/_jsx(SidebarDock, {
    containerId: containerId,
    isDesktop: isDesktop,
    isOpen: isOpen,
    setIsOpen: setIsOpen,
    isHiddenByPage: isHiddenByPage,
    hasInitialized: hasInitialized,
    loadingStatus: loadingStatus,
    coachAiName: coachAiName,
    modals: modals,
    geometry: sidebarGeometry
  });
}